import { toastr } from "react-redux-toastr";
import { useAuth0 } from "@auth0/auth0-react";
import React, { useState, useEffect } from "react";
import Pagination from "@material-ui/lab/Pagination";
import { Paper, Box, Typography } from "@material-ui/core";
// component
import Button from "components/button/Button";
import MemberService from "Lib/API/Member/index";
import AlertDialog from "components/Dialog/index";
import PastoralService from "Lib/API/PastoralApi/index";
import { ReactComponent as PlusIcon } from "assets/icons/plus.svg";
// direct import
import AddEvent from "./AddEvent";
import { columns } from "./helper";
import EventTable from "../listing-table/index";

const Events = ({
  accountId,
  fetchMyAPI,
  setPage,
  onEventChange,
  selectedFilter,
  setSelectedFilter,
  onRowClick,
  refetchEvent,
  activeClassPage,
  setActiveClassPage,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  // state
  const [list, setList] = useState([]);
  const [count, setCount] = useState(0);
  const [date, setDate] = useState([]);
  const [members, setMembers] = useState([]);
  const [addModal, setAddModal] = useState(false);
  const [eventTypes, setEventTypes] = useState([]);
  const [loading, enableLoading] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [dateRange, setDateRange] = useState([null, null]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [loadingEventTypes, setLoadingEventTypes] = useState(false);

  useEffect(() => {
    const debounce = setTimeout(() => {
      getEventList();
    }, 100);

    return () => clearTimeout(debounce);
  }, [activeClassPage, selectedFilter, dateRange, refetchEvent]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchMembers(1);
      fetchEventTypes(1);
    }, 100);

    return () => clearTimeout(timeout);
  }, [activeClassPage]);

  async function getEventList() {
    enableLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const result = await MemberService.getEventListByID({
        token: token,
        current_page: activeClassPage + 1,
        items_per_page: 30,
        data: {
          accountId: accountId,
          filter: {
            contactIds: selectedFilter?.name,
            typeIds: selectedFilter?.eventType,
            ...(dateRange[0] &&
              dateRange[1] && {
                dateRange: {
                  from: dateRange[0].toISOString().split("T")[0],
                  to: dateRange[1].toISOString().split("T")[0],
                },
              }),
            archived:
              selectedFilter?.archivedType?.length === 2
                ? "all"
                : selectedFilter?.archivedType?.[0],
          },
        },
      });
      const mappedData = result?.events?.map((event, i) => ({
        ...event,
        id: event?.typeId,
        _id: i,
      }));
      setList(mappedData);
      setCount(result?.total);
    } catch (error) {
      toastr.error("Error", error?.data?.message || "Something went wrong!");
    } finally {
      enableLoading(false);
    }
  }

  const fetchMembers = async (page) => {
    setLoadingMembers(true);
    try {
      const token = await getAccessTokenSilently();
      const response = await MemberService.getContactListByID({
        token: token,
        accountId: accountId,
      });
      const mapData = response?.contacts?.map((item) => ({
        label: item.fullName,
        value: item.id,
      }));
      setMembers(mapData ?? []);
    } catch (error) {
      console.error("Error fetching members:", error);
    } finally {
      setLoadingMembers(false);
    }
  };

  const fetchEventTypes = async (page) => {
    setLoadingEventTypes(true);
    try {
      const token = await getAccessTokenSilently();
      const response = await PastoralService.getEventTypes({
        token: token,
        page,
      });
      setEventTypes(
        response?.eventTypes?.map((item) => ({
          label: item.name,
          value: item.id,
        }))
      );
    } catch (error) {
      console.error("Error fetching event types:", error);
    } finally {
      setLoadingEventTypes(false);
    }
  };

  const confirmCloseHandler = () => {
    setAddModal(false);
    setConfirmClose(false);
  };

  const openAddModal = () => setAddModal(true);
  const closeEditModal = () => setAddModal(false);
  const closeAddModal = () => setConfirmClose(true);

  const getColumns = columns(
    members,
    eventTypes,
    date,
    setDateRange,
    setDate,
    onEventChange,
    setSelectedFilter
  );

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography style={{ fontSize: 18 }}>Events</Typography>
        <Button variant="primary" onClick={openAddModal}>
          <PlusIcon />
          <span>New Event</span>
        </Button>
      </Box>

      <Paper elevation={1} sx={{ px: 0 }}>
        <EventTable
          tasksList={list ?? []}
          columns={getColumns}
          loading={loading}
          selectedFilter={selectedFilter}
          onEventTypeFilterChange={onEventChange}
          onRowClick={onRowClick}
        />

        <Box sx={{ py: 2, display: "flex", justifyContent: "center" }}>
          <Pagination
            count={Math.ceil(count / 30)}
            color="primary"
            variant="outlined"
            onChange={(event, value) => {
              setActiveClassPage(value - 1);
            }}
            page={activeClassPage + 1}
          />
        </Box>

        {addModal && (
          <AddEvent
            open={addModal}
            closeModal={closeAddModal}
            getEventList={getEventList}
            fetchMyAPI={fetchMyAPI}
            setPage={setPage}
            members={members}
            loadingMembers={loadingMembers}
            eventTypes={eventTypes}
            loadingEventTypes={loadingEventTypes}
            closeEditModal={closeEditModal}
          />
        )}

        {confirmClose && (
          <AlertDialog
            open={confirmClose}
            onDecline={() => {
              setConfirmClose(false);
            }}
            onAccept={confirmCloseHandler}
            title={"Are you sure?"}
            description={"Do you want to discard the changes you made?"}
          />
        )}
      </Paper>
    </>
  );
};
export default Events;
